import { FileWarning, SearchX } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { useParams } from 'react-router';

import { BackLink } from '@/components/layout/BackLink';
import { GatedChrome } from '@/components/auth/GatedChrome';
import { OnlineEditorPanel } from '@/features/document-editor/components/OnlineEditorPanel';
import { useCanOpenDocuments, usePermission } from '@/hooks/usePermission';
import { NoAccessPanel } from '@/components/ui/NoAccessPanel';
import { ApiError } from '@/lib/api';
import { useActiveCompany } from '@/lib/active-company-context';
import { TO_PANEL, useBackTarget } from '@/lib/back-nav';
import { BackToListLink } from '@/features/master-list/components/BackToListLink';
import { useMasterListAccess } from '@/features/master-list/use-master-list-access';

import { ClassificationCard } from '@/features/document-classification/components/ClassificationCard';
import { ControlledFilesCard } from '@/features/document-controlled-files/components/ControlledFilesCard';
import { CustomFieldsCard } from '@/features/document-custom-fields/components/CustomFieldsCard';
import { LmsSyncCard } from '@/features/document-registration/components/LmsSyncCard';
import { OwnerCard } from '@/features/document-registration/components/OwnerCard';
import { RequestChangeCard } from '@/features/document-requests/components/RequestChangeCard';
import { RegistrationPanel } from '@/features/document-registration/components/RegistrationPanel';
import { RelatedDocumentsCard } from '@/features/document-relations/components/RelatedDocumentsCard';
import { AssociatedTrainingCard } from '@/features/document-training/components/AssociatedTrainingCard';
import { TrainingStageCard } from '@/features/document-training/components/TrainingStageCard';
import { useFlowCapabilities } from '@/features/document-editor/use-flow-capabilities';
import { useTrainingStage } from '@/features/document-editor/use-training-stage';
import { ReworkHistoryCard } from '@/features/document-rework/components/ReworkHistoryCard';
import { ResponsibilitiesCard } from '@/features/document-responsibilities/components/ResponsibilitiesCard';

import { ActionRail } from './components/ActionRail';
import { DossierHeader } from './components/DossierHeader';
import { DossierSkeleton } from './components/DossierSkeleton';
import { DossierStageCard } from './components/DossierStageCard';
import { SignatureSeal } from './components/SignatureSeal';
import { VersionTimeline } from './components/VersionTimeline';
import type { Dossier } from './types';
import { useDossier } from './use-dossier';
import { StatePanel } from '@/components/ui/StatePanel';
import { errorAdvice } from '@/lib/error-advice';

function breadcrumbFor(code: string): { label: string; to?: string; strong?: boolean }[] {
  return [
    { label: 'QMS', to: '/dashboard', strong: true },
    { label: 'Lista Maestra', to: '/documentos' },
    { label: code, strong: true },
  ];
}

/** Envuelve el chrome (breadcrumb + regreso al origen) alrededor de cualquier estado. */
function Frame({ code, children }: { code: string; children: ReactNode }): ReactElement {
  // Sin origen (deep-link) el padre jerárquico es la Lista Maestra; sin acceso a ella
  // (G4, FUN-06), el Panel, y las migas no la nombran.
  const access = useMasterListAccess();
  const listed = access.state === 'granted';
  const back = useBackTarget(listed ? { to: '/documentos', label: 'Lista Maestra' } : TO_PANEL);
  return (
    <>
      <GatedChrome
        state={access.state}
        breadcrumb={breadcrumbFor(code)}
        navContext="Ficha del documento"
      />
      <BackLink target={back} />
      {children}
    </>
  );
}

export function DocumentDossier(): ReactElement {
  const { id = '' } = useParams<{ id: string }>();
  const { activeCompanyId } = useActiveCompany();
  const query = useDossier(id, {
    companyId: activeCompanyId,
    enabled: Boolean(activeCompanyId) && id !== '',
  });

  if (!activeCompanyId || query.isLoading) {
    return (
      <Frame code="Documento">
        <DossierSkeleton />
      </Frame>
    );
  }

  if (query.isError) {
    // 404 = documento inexistente/ajeno. 400 en este GET solo puede venir de un id
    // malformado o no-positivo (deep-link/bookmark viejo): también es "no encontrado"
    // honesto, no un error de servidor con "Reintentar" que nunca funcionaría.
    const status = query.error instanceof ApiError ? query.error.status : null;
    const notFound = status === 404 || status === 400;
    return (
      <Frame code="Documento">
        {status === 403 ? (
          <NoAccessPanel title="No tienes acceso a este documento">
            Tu acceso pudo haber vencido o no incluye este documento. <BackToListLink />.
          </NoAccessPanel>
        ) : notFound ? (
          <StatePanel icon={<SearchX />} title="No encontramos este documento" role="alert">
            Puede haber sido dado de baja o no pertenece a la empresa activa. <BackToListLink />.
          </StatePanel>
        ) : (
          <StatePanel
            icon={<FileWarning />}
            title="No se pudo cargar el expediente"
            tone="danger"
            role="alert"
            onRetry={() => void query.refetch()}
          >
            {errorAdvice(query.error)}
          </StatePanel>
        )}
      </Frame>
    );
  }

  if (!query.data) {
    // Estado no-éxito remanente (p. ej. deshabilitada por empresa): skeleton seguro.
    return (
      <Frame code="Documento">
        <DossierSkeleton />
      </Frame>
    );
  }

  return (
    <Frame code={query.data.entry.code}>
      <DossierView dossier={query.data} />
    </Frame>
  );
}

function DossierView({ dossier }: { dossier: Dossier }): ReactElement {
  // Proveedor con acceso temporal: sin lectura por rol no entra al editor, así que la
  // ficha le muestra el documento en solo lectura.
  const canRead = usePermission('quality:master-list:read');
  const canOpen = useCanOpenDocuments();
  const { activeCompanyId } = useActiveCompany();
  const primary =
    dossier.responsibilities.find((r) => r.role === 'elaborador')?.displayName ?? null;
  // G4: la etapa de capacitación (estado derivado) para la insignia y su tarjeta.
  const training = useTrainingStage(dossier.entry.id, {
    companyId: activeCompanyId,
    enabled: canRead,
  });
  // "Devolver" solo si el servidor la ofrece para este documento (la misma consulta que la
  // tarjeta de la etapa: comparten caché).
  const flow = useFlowCapabilities(dossier.entry.id, {
    companyId: activeCompanyId,
    enabled: canRead && activeCompanyId !== null,
  });
  const canReturn =
    flow.data?.availableActions.some((a) => a.action === 'return' && a.enabled) ?? false;

  return (
    <>
      <DossierHeader
        entry={dossier.entry}
        primaryResponsibleName={primary}
        multinorm={dossier.norms.length > 1}
        sealed={dossier.seal !== null}
        inTraining={training.data?.state === 'in_progress'}
      />
      <div className="g-84">
        <div className="stack">
          {!canRead && canOpen && (
            <OnlineEditorPanel
              entryId={dossier.entry.id}
              code={dossier.entry.code}
              companyId={activeCompanyId}
              canEdit={false}
            />
          )}
          <VersionTimeline items={dossier.timeline} nextReviewAt={dossier.entry.nextReviewAt} />
          <ReworkHistoryCard entryId={dossier.entry.id} companyId={dossier.entry.companyId} />
          <SignatureSeal seal={dossier.seal} />
          {/* Editable, control de cambios y PDF de cada versión aprobada: solo Calidad. */}
          <ControlledFilesCard entryId={dossier.entry.id} companyId={dossier.entry.companyId} />
          {/* G2-8: ronda en curso (quién firmó o dio conformidad y quién falta). */}
          {canRead && <DossierStageCard entryId={dossier.entry.id} companyId={activeCompanyId} />}
          <ClassificationCard
            entryId={dossier.entry.id}
            code={dossier.entry.code}
            status={dossier.entry.status}
            norms={dossier.norms}
          />
          <CustomFieldsCard
            entryId={dossier.entry.id}
            code={dossier.entry.code}
            status={dossier.entry.status}
            fields={dossier.customFields}
          />
          {canRead && (
            <RelatedDocumentsCard
              entryId={dossier.entry.id}
              code={dossier.entry.code}
              status={dossier.entry.status}
            />
          )}
        </div>
        <div className="stack">
          <RegistrationPanel entry={dossier.entry} />
          <LmsSyncCard entry={dossier.entry} sync={dossier.lmsSync} />
          <ActionRail
            entryId={dossier.entry.id}
            code={dossier.entry.code}
            print={dossier.documentCapabilities.print}
            canReturn={canReturn}
          />
          <RequestChangeCard entry={dossier.entry} />
          <OwnerCard entry={dossier.entry} />
          <ResponsibilitiesCard
            entryId={dossier.entry.id}
            code={dossier.entry.code}
            status={dossier.entry.status}
            responsibilities={dossier.responsibilities}
          />
          {canRead && (
            <TrainingStageCard
              entryId={dossier.entry.id}
              code={dossier.entry.code}
              companyId={activeCompanyId}
            />
          )}
          <AssociatedTrainingCard
            entryId={dossier.entry.id}
            code={dossier.entry.code}
            status={dossier.entry.status}
            companyId={dossier.entry.companyId}
          />
        </div>
      </div>
    </>
  );
}
